import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import type { AssetKind, TrackKind } from "./meetingAssets";
import { parseMeetingLink } from "./meetingLink";

const RING_MS = 2000;

const TRACK_KINDS: readonly AssetKind[] = ["screen", "camera", "audio"];

/**
 * What a meeting link asked for, read from the page URL: the asset to open on,
 * the moment to seek to, and whether a panel should still be ringed to show
 * where the link landed (for two seconds after the page opens).
 */
export function useMeetingLinkTarget() {
  const [params] = useSearchParams();
  const { asset, t, summary } = parseMeetingLink(params);
  const [ringing, setRinging] = useState(!!asset);

  useEffect(() => {
    if (!asset) return;
    setRinging(true);
    const timer = setTimeout(() => setRinging(false), RING_MS);
    return () => clearTimeout(timer);
  }, [asset]);

  const trackKind = TRACK_KINDS.includes(asset as AssetKind)
    ? (asset as TrackKind)
    : undefined;

  return {
    asset,
    t,
    summary,
    trackKind,
    /** Classes for the panel showing `kinds`, while the link's ring is on. */
    ringFor: (...kinds: AssetKind[]) =>
      ringing && asset && kinds.includes(asset)
        ? "ring-2 ring-primary ring-offset-2 ring-offset-background rounded-lg transition-shadow"
        : "",
  };
}
